import { useMemo, useState } from "react";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import { ClipboardList, SlidersHorizontal } from "lucide-react";
import { DataTable, StatusBadge, type Column, type StatusTone } from "@fawkes/ui";

import { speciesIconSrc } from "../components/AnimalOverview";
import { useTabelIndeling } from "../hooks/useTabelIndeling";
import { formatDate } from "../i18n/format";

// wachtlijst-afstandsportaal: het overzicht van afstandsaanvragen. De eigenaar vult het formulier in op
// het platform; dit is de kant van het asiel.
//
// Dezelfde schil als Aanwezig en de andere opvangoverzichten (`da-overview`): hij staat in dezelfde
// schakelbalk, dus hij hoort er ook hetzelfde uit te zien — tellers bovenin, één filterrij, en dezelfde
// lijst/tegels-schakelaar die onthoudt wat je koos.
//
// Twee kolommen die uit elkaar gehouden moeten worden, en dat is bewust:
//   - Status intake  — wat ervan geworden is. Rood/oranje/groen, en een medewerker mag hem overrulen.
//   - Dossier compleet — hoeveel van de verplichte vragen beantwoord is. Puur tellen; de server rekent
//     het uit, wij kleuren het alleen. Leeg betekent "geen vragenlijst gekoppeld" en is dus iets anders
//     dan 0%.

interface DierRegel {
  id: number;
  naam: string | null;
  diersoortId: number | null;
  diersoort: string | null;
  ras: string | null;
  geslacht: string | null;
  geboortedatum: string | null;
  chipPasnummer: string | null;
  compleetheid: number | null;
  dierId: number | null;
}

interface Regel {
  aanvraagId: string;
  nummer: string | null;
  vestigingId: number;
  status: string;
  stoplicht: "ROOD" | "ORANJE" | "GROEN";
  statusHandmatig: boolean;
  compleetheid: number | null;
  contactNaam: string | null;
  klantId: number | null;
  ontvangenOp: string;
  ingediendOp: string | null;
  dieren: DierRegel[];
}

interface Tellers {
  nieuw: number;
  actieVereist: number;
  klaarVoorBeoordeling: number;
  gepland: number;
}

interface Pagina {
  regels: Regel[];
  tellers: Tellers;
}

type Weergave = "card" | "compact" | "list";

const PREFS_KEY = "fawkes.wachtlijst.weergave";

const STATUSSEN = [
  "NIEUW",
  "ACTIE_VEREIST",
  "IN_BEHANDELING",
  "COMPLEET",
  "GEPLAND",
  "AFGEHANDELD",
  "AFGEWEZEN",
  "INGETROKKEN",
  "VERLOPEN",
] as const;

/** Het stoplicht van de server naar de toon van de badge; één vertaling, niet per scherm opnieuw. */
const TOON: Record<Regel["stoplicht"], StatusTone> = {
  ROOD: "danger",
  ORANJE: "warning",
  GROEN: "success",
};

/**
 * De kleur van de stip achter het percentage. Volledig is groen, half of meer oranje, daaronder rood —
 * dezelfde drempels als het stoplicht, zodat een regel niet een groene stip naast een rode status krijgt.
 */
function stipToon(percentage: number): StatusTone {
  if (percentage >= 100) return "success";
  if (percentage >= 50) return "warning";
  return "danger";
}

/** "Bobby · Hond" bij één dier, "Bobby, Mimi" bij meer — dat is waar de balie op zoekt. */
function dierenTekst(dieren: DierRegel[]): string {
  if (dieren.length === 0) return "—";
  if (dieren.length === 1) {
    const d = dieren[0];
    return [d.naam ?? "—", d.diersoort].filter(Boolean).join(" · ");
  }
  return dieren.map((d) => d.naam ?? "—").join(", ");
}

function leesWeergave(): Weergave {
  try {
    const bewaard = localStorage.getItem(PREFS_KEY);
    if (bewaard === "card" || bewaard === "compact" || bewaard === "list") return bewaard;
  } catch {
    // Een browser zonder opslag (privévenster) mag dit scherm niet breken.
  }
  return "list";
}

export function WachtlijstList({ onOpen, onInstellingen }: {
  onOpen?: (aanvraagId: string) => void;
  /** Naar de instellingen van het afstandsportaal — welke vragen de eigenaar krijgt. */
  onInstellingen?: () => void;
} = {}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { indeling, setIndeling } = useTabelIndeling("wachtlijst");

  const { data, isLoading, isError, isFetching } = useCustom<Pagina>({
    url: "wachtlijst",
    method: "get",
  });
  const pagina = data?.data;
  const regels = useMemo(() => pagina?.regels ?? [], [pagina]);
  const tellers = pagina?.tellers;

  const [zoek, setZoek] = useState("");
  const [status, setStatus] = useState("");
  const [weergave, setWeergave] = useState<Weergave>(leesWeergave);

  const bewaarWeergave = (m: Weergave) => {
    setWeergave(m);
    try {
      localStorage.setItem(PREFS_KEY, m);
    } catch {
      // Zie leesWeergave: niet kunnen onthouden is geen reden om niet te kunnen wisselen.
    }
  };

  const gefilterd = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    return regels.filter((r) => {
      if (status && r.status !== status) return false;
      if (!q) return true;
      return (
        (r.nummer ?? "").toLowerCase().includes(q) ||
        (r.contactNaam ?? "").toLowerCase().includes(q) ||
        r.dieren.some((d) => (d.naam ?? "").toLowerCase().includes(q))
      );
    });
  }, [regels, zoek, status]);

  // De tellers zijn knoppen: erop klikken filtert, nog een keer zet het filter uit. Zo hoeft niemand
  // eerst een getal te lezen en daarna hetzelfde in een keuzelijst te zoeken.
  const kaarten = [
    { key: "nieuw", waarde: tellers?.nieuw ?? 0, status: "NIEUW" },
    { key: "actieVereist", waarde: tellers?.actieVereist ?? 0, status: "ACTIE_VEREIST" },
    { key: "klaar", waarde: tellers?.klaarVoorBeoordeling ?? 0, status: "COMPLEET" },
    { key: "gepland", waarde: tellers?.gepland ?? 0, status: "GEPLAND" },
  ] as const;

  const columns: Column<Regel>[] = [
    { header: t("wachtlijst.col.nummer"), cell: (r) => r.nummer ?? "—" },
    { header: t("wachtlijst.col.dieren"), cell: (r) => dierenTekst(r.dieren) },
    { header: t("wachtlijst.col.eigenaar"), cell: (r) => r.contactNaam ?? "—" },
    {
      header: t("wachtlijst.col.status"),
      cell: (r) => (
        <span className="wachtlijst__status">
          <StatusBadge tone={TOON[r.stoplicht]}>{t(`wachtlijst.status.${r.status}`)}</StatusBadge>
          {/* Zichtbaar laten dat een mens hem heeft gezet: de telling overschrijft hem daarna niet meer. */}
          {r.statusHandmatig && (
            <span className="wachtlijst__handmatig" title={t("wachtlijst.handmatig")} aria-label={t("wachtlijst.handmatig")}>
              ✎
            </span>
          )}
        </span>
      ),
    },
    { header: t("wachtlijst.col.compleet"), cell: (r) => <Compleetheid waarde={r.compleetheid} t={t} /> },
    { header: t("wachtlijst.col.ontvangen"), cell: (r) => formatDate(r.ontvangenOp, locale) },
  ];

  const lijstLeeg = isLoading ? t("common.loading") : t("wachtlijst.leeg");

  return (
    <div className="da-overview wachtlijst">
      <div className="da-overview__header">
        <div className="da-overview__intro">
          <span className="da-overview__icon" aria-hidden="true"><ClipboardList size={30} /></span>
          <div>
            <h1 className="da-overview__title">{t("wachtlijst.titel")}</h1>
            <p className="da-overview__subtitle">{t("wachtlijst.onderschrift")}</p>
          </div>
        </div>
        <div className="da-overview__stats">
          {kaarten.map((k) => (
            <button
              key={k.key}
              type="button"
              className={`da-overview__stat${status === k.status ? " da-overview__stat--active" : ""}`}
              aria-pressed={status === k.status}
              onClick={() => setStatus(status === k.status ? "" : k.status)}
            >
              <span className="da-overview__stat-body">
                <span className="da-overview__stat-value">{k.waarde}</span>
                <span className="da-overview__stat-label">{t(`wachtlijst.kpi.${k.key}`)}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="da-overview__filters">
        <div className="da-overview__filter-grid">
          <div className="fawkes-products-search da-overview__search">
            <span className="fawkes-products-search__icon" aria-hidden="true">🔍</span>
            <input
              type="search"
              autoComplete="off"
              className="fawkes-products-search__input"
              placeholder={t("wachtlijst.zoek")}
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
              aria-label={t("wachtlijst.zoek")}
            />
          </div>
          <div className="da-overview__field">
            <span className="da-overview__field-label">{t("wachtlijst.col.status")}</span>
            <select
              className="da-overview__filter"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label={t("wachtlijst.col.status")}
            >
              <option value="">{t("wachtlijst.filter.alle")}</option>
              {STATUSSEN.map((s) => (
                <option key={s} value={s}>{t(`wachtlijst.status.${s}`)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {onInstellingen && (
        <div className="kvo__beheer">
          <button type="button" className="kvo__instellingen" onClick={onInstellingen}>
            <SlidersHorizontal size={16} aria-hidden="true" />
            {t("wachtlijst.instellingen")}
          </button>
        </div>
      )}

      <div className="da-overview__resultsbar">
        <span className="da-overview__results-count">
          <ClipboardList size={16} /> {gefilterd.length}{" "}
          {t(gefilterd.length === 1 ? "wachtlijst.results.foundOne" : "wachtlijst.results.found")}
        </span>
        <div className="da-overview__resultsbar-right">
          <div className="da-overview__viewtoggle" role="group" aria-label={t("wachtlijst.weergave")}>
            {(["card", "compact", "list"] as const).map((m) => (
              <button
                key={m}
                type="button"
                className={`da-overview__viewbtn${weergave === m ? " da-overview__viewbtn--active" : ""}`}
                onClick={() => bewaarWeergave(m)}
                title={t(`wachtlijst.weergave.${m}`)}
                aria-label={t(`wachtlijst.weergave.${m}`)}
                aria-pressed={weergave === m}
              >
                <span aria-hidden="true">{m === "card" ? "▦" : m === "compact" ? "▥" : "☰"}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tijdens een verversing blijft de vorige lijst staan, iets gedimd — zo zie je dat er iets
          gebeurt zonder dat het scherm leegslaat. Zelfde gedrag als Aanwezig. */}
      <div className={isFetching && !isLoading ? "da-overview__body da-overview__body--verversen" : "da-overview__body"}>
        {isError ? (
          <p className="fawkes-error">{t("wachtlijst.laadfout")}</p>
        ) : weergave === "list" ? (
          <DataTable<Regel>
            columns={columns}
            rows={gefilterd}
            getRowKey={(r) => r.aanvraagId}
            empty={lijstLeeg}
            onRowClick={onOpen ? (r) => onOpen(r.aanvraagId) : undefined}
            indeling={indeling}
            onIndelingChange={setIndeling}
          />
        ) : gefilterd.length === 0 ? (
          <p className="fawkes-muted">{lijstLeeg}</p>
        ) : (
          <div className={`da-overview__grid${weergave === "compact" ? " da-overview__grid--compact" : ""}`}>
            {gefilterd.map((r) => (
              <Kaart key={r.aanvraagId} regel={r} locale={locale} t={t} onOpen={onOpen} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Het percentage met een stip ervoor; leeg betekent "geen vragenlijst", niet "0%". */
function Compleetheid({ waarde, t }: { waarde: number | null; t: ReturnType<typeof useTranslate> }) {
  if (waarde == null) {
    return <span className="wachtlijst__geen-telling" title={t("wachtlijst.geenTelling")}>—</span>;
  }
  return (
    <span className="wachtlijst__compleet">
      <span className={`wachtlijst__stip wachtlijst__stip--${stipToon(waarde)}`} aria-hidden="true" />
      {waarde}%
    </span>
  );
}

/** Eén aanvraag als tegel, in dezelfde vorm als de dierkaarten van Aanwezig. */
function Kaart({
  regel,
  locale,
  t,
  onOpen,
}: {
  regel: Regel;
  locale: string;
  t: ReturnType<typeof useTranslate>;
  onOpen?: (aanvraagId: string) => void;
}) {
  const eerste = regel.dieren[0];
  return (
    <button
      type="button"
      className="wachtlijst__kaart"
      onClick={() => onOpen?.(regel.aanvraagId)}
      disabled={!onOpen}
    >
      <span className="wachtlijst__kaart-kop">
        {eerste?.diersoort && (
          <img className="wachtlijst__kaart-icoon" src={speciesIconSrc(eerste.diersoort)} alt="" />
        )}
        <span className="wachtlijst__kaart-titel">{dierenTekst(regel.dieren)}</span>
        <StatusBadge tone={TOON[regel.stoplicht]}>{t(`wachtlijst.status.${regel.status}`)}</StatusBadge>
      </span>
      <span className="wachtlijst__kaart-regel">{regel.nummer ?? "—"} · {regel.contactNaam ?? "—"}</span>
      <span className="wachtlijst__kaart-regel">
        <Compleetheid waarde={regel.compleetheid} t={t} />
        <span className="wachtlijst__kaart-datum">{formatDate(regel.ontvangenOp, locale)}</span>
      </span>
    </button>
  );
}
